import React, { useEffect, useMemo, useState } from "react";
import { CARD_LABEL, META_SIZE, MIN_TEXT, ROW_SIZE } from "../utils/homeType";
import { ChevronDown, ChevronUp } from "lucide-react";
import LineupBanner from "./LineupBanner";
import { useAuth } from "../context/AuthContext";
import { getTeamInitials } from "../utils/teamName";
import { useTeamOwnership } from "../hooks/useTeamOwnership";
import { buildGamesBySlug } from "../utils/gameStatus";
import { resolveLineupWeek } from "../utils/season";
import { fetchWeekGames } from "../utils/weekGames";
import { formatGameLine, latestScoredWeek, ownershipBand, rankTopTeams, seasonThroughWeek, TOP_TEAMS_COLLAPSED, TOP_TEAMS_LIMIT } from "../utils/topTeams";

// The twenty five FBS teams scoring the most fantasy points, for the latest
// scored week or the season so far. Ten show until the list is expanded.
// Site-wide, not league-scoped: every league scores a team the same way, so
// this is the one leaderboard that means the same thing to everyone on /home,
// including a user with no league yet.
//
// Reads the AuthContext teams map, which already carries weekly_points and
// game_points for every team, so the points cost no query. That map loads at
// sign-in, so during a live Saturday it shows the scores as they were at
// launch. Season totals stop at the last finished week (seasonThroughWeek).
//
// Ownership is the one cross-league read, cached for hours by
// useTeamOwnership. It describes this week's rosters, whichever tab is open.
// If it fails to load the column is left out rather than showing zeros.
//
// The Week tab's game lines come from fetchWeekGames, the cached reader the
// Scoreboard and This Week's games already share. A week behind the current
// one cannot change, so it is persisted for a day and loads once. If the
// fetch fails the rows simply have no second line.
//
// Rows do not open the TeamDrawer: the drawer reads LeagueContext and
// DraftContext, and /home sits outside every league.

const TABS = [
  { key: "week", label: "Week" },
  { key: "season", label: "Season" },
];

// Numbers read in the same type as the team name beside them, at 13px, the
// body size the rest of /home uses (league card detail, fixtures, news).
const ROW_TEXT = { fontSize: ROW_SIZE, fontWeight: 600, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" };
// Numbers and their column labels run smaller than the team name: three
// columns plus a long school name do not fit a 390px phone at 13px, and the
// name is what a reader scans.
const STAT_TEXT = { fontSize: 11, fontWeight: 600, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" };
const HEAD_TEXT = CARD_LABEL;
const COL_TOTAL = 54;
const COL_AVG = 34;

function TeamLogo({ row }) {
  const [failed, setFailed] = useState(false);
  if (failed || !row.logo) {
    return (
      <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "var(--color-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", flexShrink: 0 }}>
        {getTeamInitials(row.name)}
      </div>
    );
  }
  return <img src={row.logo} alt="" onError={() => setFailed(true)} style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} />;
}

function TopTeamsCard() {
  const { teams, seasonConfig } = useAuth();
  const [mode, setMode] = useState("week");
  const [expanded, setExpanded] = useState(false);

  const currentWeek = seasonConfig?.currentWeek;
  const ownership = useTeamOwnership(resolveLineupWeek(currentWeek));
  const showOwnership = ownership !== null;

  const week = useMemo(() => latestScoredWeek(teams), [teams]);
  const throughWeek = useMemo(() => seasonThroughWeek(currentWeek, week), [currentWeek, week]);

  const [gamesBySlug, setGamesBySlug] = useState({});
  useEffect(() => {
    if (week === null) return undefined;
    let cancelled = false;
    const isFinished = week < resolveLineupWeek(currentWeek);
    fetchWeekGames(week, { isFinished })
      .then((rows) => { if (!cancelled) setGamesBySlug(buildGamesBySlug(rows)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [week, currentWeek]);
  const rows = useMemo(() => {
    if (week === null) return [];
    if (mode === "season" && throughWeek === null) return [];
    return rankTopTeams(teams, { mode, week, throughWeek, ownership: ownership || {} });
  }, [teams, mode, week, throughWeek, ownership]);

  // Nothing scored yet: no card.
  if (week === null) return null;

  return (
    <div style={{ marginTop: 32, backgroundColor: "var(--color-bg-card)", border: "1.5px solid var(--color-bg-subtle)", borderRadius: 16, overflow: "hidden" }}>
      {/* The Home tour points at the title and the Week / Season switch, not
          the whole card: open, the list runs taller than a phone screen, and
          a spotlight on all of it leaves nowhere on screen for the popover. */}
      <div data-tour="home-top25">
      <LineupBanner title={`NCAA Top ${TOP_TEAMS_LIMIT}`} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 14px" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
          {mode === "week" ? `Week ${week}` : throughWeek ? `Season, Through Week ${throughWeek}` : "Season"}
        </span>
        <div role="tablist" style={{ display: "flex", backgroundColor: "var(--color-bg-subtle)", borderRadius: 8, padding: 2, flexShrink: 0 }}>
          {TABS.map((tab) => {
            const active = mode === tab.key;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={active}
                onClick={() => setMode(tab.key)}
                style={{ padding: "4px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", backgroundColor: active ? "var(--color-bg-card)" : "transparent", color: active ? "var(--color-text-strong)" : "var(--color-text-muted)", boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      </div>

      <div style={{ borderTop: "1px solid var(--color-bg-subtle)" }}>
        {rows.length === 0 ? (
          <div style={{ padding: "14px 16px", fontSize: 13, color: "var(--color-text-faint)" }}>
            {mode === "season" ? "Season totals appear once the first week is complete." : "No teams have scored yet."}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px 6px 82px", ...HEAD_TEXT }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  Team
                  {/* Smaller than the label it qualifies, so it fits beside
                      the number columns at phone width. */}
                  {showOwnership && <span style={{ fontSize: MIN_TEXT, fontWeight: 700, letterSpacing: "0.02em", marginLeft: 3 }}>(Global Ownership %)</span>}
                </span>
                {mode === "season" ? (
                  <>
                    <span style={{ width: COL_TOTAL, textAlign: "right", flexShrink: 0 }}>Pts (GP)</span>
                    <span style={{ width: COL_AVG, textAlign: "right", flexShrink: 0 }}>Avg</span>
                  </>
                ) : (
                  <span style={{ width: COL_AVG, textAlign: "right", flexShrink: 0 }}>Pts</span>
                )}
              </div>
            {(expanded ? rows : rows.slice(0, TOP_TEAMS_COLLAPSED)).map((row, i) => (
              <div
                key={row.slug}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderTop: "1px solid var(--color-bg-subtle)" }}
              >
                <span style={{ width: 20, fontSize: 13, fontWeight: 700, color: "var(--color-text-faint)", textAlign: "right", flexShrink: 0 }}>
                  {i > 0 && rows[i - 1].rank === row.rank ? "" : row.rank}
                </span>
                <TeamLogo row={row} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* The name gives way to the ellipsis, the bracket never does:
                      a long school should lose letters, not its ownership. */}
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, minWidth: 0 }}>
                    <span style={{ ...ROW_TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                      {row.name}
                    </span>
                    {showOwnership && (
                      <span style={{ ...ROW_TEXT, fontSize: MIN_TEXT, fontWeight: 500, color: "var(--color-text-muted)", whiteSpace: "nowrap", flexShrink: 0 }}>
                        ({ownershipBand(row.ownership)})
                      </span>
                    )}
                  </div>
                  {mode === "week" && formatGameLine(gamesBySlug[row.slug], teams) && (
                    <div style={{ fontSize: META_SIZE, color: "var(--color-text-muted)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontVariantNumeric: "tabular-nums" }}>
                      {formatGameLine(gamesBySlug[row.slug], teams)}
                    </div>
                  )}
                </div>
                {mode === "season" ? (
                  <>
                    <span style={{ ...STAT_TEXT, width: COL_TOTAL, textAlign: "right", flexShrink: 0 }}>
                      {row.points} ({row.gamesPlayed})
                    </span>
                    <span style={{ ...STAT_TEXT, width: COL_AVG, textAlign: "right", flexShrink: 0 }}>
                      {row.average.toFixed(1)}
                    </span>
                  </>
                ) : (
                  <span style={{ ...STAT_TEXT, width: COL_AVG, textAlign: "right", flexShrink: 0 }}>{row.points}</span>
                )}
              </div>
            ))}
            {rows.length > TOP_TEAMS_COLLAPSED && (
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "10px 0", backgroundColor: "var(--color-bg-card)", border: "none", borderTop: "1px solid var(--color-bg-subtle)", color: "var(--color-brand)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {expanded ? "Show top 10" : `Show ${TOP_TEAMS_COLLAPSED + 1} to ${rows.length}`}
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
          </>
        )}
      </div>

      {mode === "season" && (
        <div style={{ padding: "10px 14px 12px", borderTop: "1px solid var(--color-bg-subtle)", fontSize: 12, color: "var(--color-text-muted)" }}>
          Season stats cover completed weeks only and update when each game week ends.
        </div>
      )}
    </div>
  );
}

export default TopTeamsCard;
